<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaAvatar
              :size="32"
              :src="getAvatarUrl"
              :fallback-text="getUserName.charAt(0).toUpperCase()"
              color="primary"
            />
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import authService from '../../../../services/auth.service'
import { getImageURL } from '../../../../config/api'

// 用户信息
const currentUser = ref<any>(null)
const loading = ref(true)
let userUpdateHandler: ((event: CustomEvent) => void) | null = null

// 获取当前用户信息
const fetchCurrentUser = async (retryCount = 0) => {
  try {
    // 检查是否有token
    const token = localStorage.getItem('token')

    if (!token) {
      loading.value = false
      return
    }

    // 强制从 API 获取最新的用户信息
    const response = await authService.getProfile()

    if (response.data.success) {
      currentUser.value = response.data.data
      // 同步更新 localStorage
      localStorage.setItem('user', JSON.stringify(currentUser.value))
    }
  } catch (error: any) {
    // 如果是401错误且是第一次尝试，延迟后重试
    if (error.response?.status === 401 && retryCount < 2) {
      setTimeout(() => {
        fetchCurrentUser(retryCount + 1)
      }, 2000)
      return
    }
  } finally {
    if (retryCount === 0) {
      loading.value = false
    }
  }
}

// 获取头像URL（使用集中配置）
const getAvatarUrl = computed(() => {
  if (!currentUser.value?.avatar) {
    console.log('ProfileDropdown: 没有头像', currentUser.value)
    return ''
  }
  const url = getImageURL(currentUser.value.avatar)
  console.log('ProfileDropdown: 头像处理', {
    original: currentUser.value.avatar,
    processed: url,
  })
  return url
})

// 获取用户名
const getUserName = computed(() => {
  const name = currentUser.value?.nickname || currentUser.value?.username || '用户'
  // console.log('ProfileDropdown: 计算用户名:', name, 'currentUser:', currentUser.value)
  return name
})

onMounted(() => {
  // 强制从 API 获取最新的用户信息
  fetchCurrentUser()

  // 监听用户信息更新事件
  userUpdateHandler = (event: CustomEvent) => {
    console.log('ProfileDropdown: 收到用户信息更新事件', event.detail)
    // 从 localStorage 重新读取用户信息
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        currentUser.value = JSON.parse(storedUser)
        console.log('ProfileDropdown: 已更新用户信息', currentUser.value)
      } catch (e) {
        console.error('ProfileDropdown: 解析用户信息失败', e)
      }
    }
  }

  window.addEventListener('user-profile-updated', userUpdateHandler as EventListener)
})

onUnmounted(() => {
  // 清理事件监听器
  if (userUpdateHandler) {
    window.removeEventListener('user-profile-updated', userUpdateHandler as EventListener)
  }
})

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  click?: () => void
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'user-profile',
            icon: 'account_circle',
          },
          {
            name: 'settings',
            to: 'settings',
            icon: 'settings',
          },
          {
            name: 'billing',
            to: 'billing',
            icon: 'receipt_long',
          },
          {
            name: 'projects',
            to: 'projects',
            icon: 'favorite',
          },
        ],
      },
      {
        name: 'explore',
        separator: true,
        list: [
          {
            name: 'faq',
            to: 'faq',
            icon: 'quiz',
          },
          {
            name: 'helpAndSupport',
            href: 'https://discord.gg/u7fQdqQt8c',
            icon: 'help',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            icon: 'logout',
            click: () => authService.logout(),
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.click) {
    return { onClick: item.click }
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
